import { Component, Host, h, Prop, State } from '@stencil/core';
import { gettrackmeEventCategory, gettrackmeEventObject } from '../../../utils/tracking-helpers';
import { fetchComponents, fetchCountries, fetchRegulations } from './sew-ws-lexfinder.service';
import { SelectOption } from '../../forms/sew-combobox/sew-combobox.types';
import { SewComboboxCustomEvent } from '../../../components';

@Component({
    tag: 'sew-ws-lexfinder',
    styleUrl: 'sew-ws-lexfinder.scss',
    shadow: true,
})
export class SewWsLexfinder {
    @Prop() text: string;
    @Prop() language: string;
    @Prop() apiBaseUrl = 'https://caas.sew-d.com/direct/SEWDE';
    @Prop() resultBaseUrl = '/international-regulations/result';
    @Prop() buttonText: string;
    @Prop() regulationLabel: string;
    @Prop() countryLabel: string;
    @Prop() componentLabel: string;
    @Prop() servicesItemLabel: string;

    @State() convertedCountries: { name: string; id: number }[] = [];
    @State() convertedComponents: { name: string; id: number }[] = [];
    @State() convertedRegulations: { name: string; id: number }[] = [];
    @State() currentSelectedCountry: { name: string; id: number };
    @State() currentSelectedComponent: { name: string; id: number };
    @State() currentSelectedRegulation: { name: string; id: number };
    @State() countryDisabled = true;
    @State() componentsDisabled = true;
    @State() regulationsDisabled = true;
    @State() linkDisabled = true;

    private componentDropdownRef: HTMLSelectElement;
    private regulationDropdownRef: HTMLSelectElement;

    private getCountries(fetchedCountries: []): void {
        const convertedCountries = [];

        fetchedCountries.map((country: { _id }) =>
            convertedCountries.push({
                name: country._id.name,
                id: country._id.country_id,
                value: country._id.country_id,
            })
        );

        this.convertedCountries = convertedCountries;
    }

    private getComponents(fetchedComponents: []): void {
        const convertedComponents = [];

        fetchedComponents.map((component: { _id }) =>
            convertedComponents.push({
                name: component._id.name,
                id: component._id.component_id,
                value: component._id.component_id,
            })
        );

        this.convertedComponents = convertedComponents;
    }

    private getRegulations(fetchedRegulations: []): void {
        const convertedRegulations = [];

        fetchedRegulations.map((regulation: { shortTitle; _id }) =>
            convertedRegulations.push({
                name: regulation.shortTitle,
                id: regulation._id,
                value: regulation._id,
            })
        );

        this.convertedRegulations = convertedRegulations;
    }

    private generateLink(): void {
        const resultParams = `${this.currentSelectedCountry.name}/${this.currentSelectedComponent.name}/${this.currentSelectedRegulation.name}`;
        const queryParams = `language=${this.language}`;

        location.href = encodeURI(`${this.resultBaseUrl}/${resultParams}?${queryParams}`);
    }

    componentWillLoad(): void {
        // handle empty property same as default
        this.resultBaseUrl =
            this.resultBaseUrl.length > 0 ? this.resultBaseUrl : '/international-regulations/result';

        fetchCountries(this.apiBaseUrl, this.language)
            .then(res => {
                this.getCountries(res);
                this.countryDisabled = false;
            })
            .catch(err => {
                console.error(err);
                this.countryDisabled = true;
            });
    }

    render() {
        return (
            <Host>
                <div class="main-content-container">
                    <p>{this.text}</p>

                    <sew-combobox
                        ws
                        comboboxId="country_dropdown"
                        placeholder={this.countryLabel}
                        onItemSelected={(selectedItem: SewComboboxCustomEvent<SelectOption>) => {
                            this.componentDropdownRef.value = '';
                            this.regulationDropdownRef.value = '';
                            this.componentsDisabled = true;
                            this.regulationsDisabled = true;
                            this.linkDisabled = true;
                            this.currentSelectedCountry = {
                                name: selectedItem.detail.name,
                                id: Number(selectedItem.detail.value),
                            };

                            fetchComponents(this.apiBaseUrl, this.language, this.currentSelectedCountry.id)
                                .then(res => {
                                    this.getComponents(res);
                                    this.componentsDisabled = false;
                                    this.regulationsDisabled = true;
                                    this.linkDisabled = true;
                                })
                                .catch(err => {
                                    //@TODO proper error handlings
                                    console.error(err);
                                });
                        }}
                    >
                        <select>
                            <option></option>
                            {this.convertedCountries.map(item => (
                                <option value={item.id}>{item.name}</option>
                            ))}
                        </select>
                    </sew-combobox>

                    <sew-combobox
                        ws
                        comboboxId="components_dropdown"
                        placeholder={this.componentLabel}
                        disabled={this.componentsDisabled}
                        noSearch
                        onItemSelected={(selectedItem: SewComboboxCustomEvent<SelectOption>) => {
                            this.currentSelectedComponent = {
                                name: selectedItem.detail.name,
                                id: Number(selectedItem.detail.value),
                            };
                            this.regulationDropdownRef.value = '';
                            this.regulationsDisabled = true;
                            this.linkDisabled = true;

                            selectedItem.detail.value &&
                                fetchRegulations(
                                    this.apiBaseUrl,
                                    this.language,
                                    this.currentSelectedCountry.id,
                                    this.currentSelectedComponent.id
                                )
                                    .then(res => {
                                        this.getRegulations(res);
                                        this.regulationsDisabled = false;
                                        this.linkDisabled = true;
                                    })
                                    .catch(err => {
                                        //@TODO proper error handlings
                                        console.error(err);
                                    });
                        }}
                    >
                        <select ref={ref => (this.componentDropdownRef = ref)}>
                            <option></option>
                            {this.convertedComponents.map(item => (
                                <option value={item.id}>{item.name}</option>
                            ))}
                        </select>
                    </sew-combobox>

                    <sew-combobox
                        ws
                        comboboxId="regulations_dropdown"
                        placeholder={this.regulationLabel}
                        disabled={this.regulationsDisabled}
                        noSearch
                        onItemSelected={(selectedItem: SewComboboxCustomEvent<SelectOption>) => {
                            this.currentSelectedRegulation = {
                                name: selectedItem.detail.name,
                                id: Number(selectedItem.detail.value),
                            };

                            this.linkDisabled = !selectedItem.detail.value;
                        }}
                    >
                        <select ref={ref => (this.regulationDropdownRef = ref)}>
                            <option></option>
                            {this.convertedRegulations.map(item => (
                                <option value={item.id}>{item.name}</option>
                            ))}
                        </select>
                    </sew-combobox>

                    <sew-ws-button>
                        <button
                            onClick={() => this.generateLink()}
                            disabled={this.linkDisabled}
                            data-sew-trackme-event-category={gettrackmeEventCategory('Services')}
                            data-sew-trackme-event-object={gettrackmeEventObject(
                                `Services_${this.servicesItemLabel}_${this.buttonText}`
                            )}
                            data-sew-trackme-event-action="Click"
                        >
                            {this.buttonText}
                        </button>
                    </sew-ws-button>
                </div>
            </Host>
        );
    }
}
